/**
 * Data access for the RAISE Curriculum feature.
 *
 * Talks to the Plus AppSync API via two operations:
 *   - listRaiseCurriculum(input: AWSJSON!)   read  (query)
 *   - manageRaiseCurriculum(input: AWSJSON!) write (mutation)
 *
 * Every call sends a single AWSJSON `input` = { action, data }. The specific
 * operation is carried in `action`; the resolver Lambda owns shape/validation
 * and authorization (writes require RAISE admin, enforced server-side).
 *
 * Reads are not cached — each read hits the resolver so the UI always reflects
 * current backend state.
 */

import { callPlusGraphQLDirect } from "src/utilities/AWSServices";

/**
 * Parse a RAISE `session_date` into a Date at LOCAL midnight.
 *
 * `session_date` is a SQL `date` (date-only, e.g. "2026-10-21") with no
 * time-of-day. `new Date("2026-10-21")` parses that as midnight UTC, so when
 * rendered in a timezone behind UTC (e.g. Seattle, UTC-7/-8) it rolls back to
 * the previous calendar day — the session shows up a day early. Splitting the
 * parts and using the local Date constructor pins it to the intended calendar
 * day in every timezone.
 *
 * Accepts "YYYY-MM-DD" or "YYYY/MM/DD" (the admin DatePicker emits the latter).
 * Falls back to `new Date(value)` for any other/empty input so non-date inputs
 * still behave as before. Returns an Invalid Date (not a throw) on garbage,
 * matching `new Date` semantics callers already guard against.
 */
export const parseSessionDate = (value) => {
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  return new Date(value);
};

const READ_QUERY = `query listRaiseCurriculum($input: AWSJSON!) {
  listRaiseCurriculum(input: $input)
}`;

const WRITE_MUTATION = `mutation manageRaiseCurriculum($input: AWSJSON!) {
  manageRaiseCurriculum(input: $input)
}`;

/**
 * Plus AppSync returns an AWSJSON scalar serialized as a JSON string, and the
 * response VTL runs $util.toJson on a payload the Lambda already json.dumps'd,
 * so the value can come back double-encoded. Parse repeatedly until we reach a
 * non-string (object/array).
 */
const parseResult = (response, fieldName) => {
  let raw = response?.data ? response.data[fieldName] : null;
  if (raw == null) return null;
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      break;
    }
  }
  return raw;
};

const runRead = async (action, data = {}) => {
  const response = await callPlusGraphQLDirect(READ_QUERY, {
    input: JSON.stringify({ action, data }),
  });
  return parseResult(response, "listRaiseCurriculum");
};

const runWrite = async (action, data = {}) => {
  const response = await callPlusGraphQLDirect(WRITE_MUTATION, {
    input: JSON.stringify({ action, data }),
  });
  return parseResult(response, "manageRaiseCurriculum");
};

/* ---------------- reads ----------------
 * No client-side caching: every read hits the resolver directly so the UI
 * always reflects the current backend state. */

// One call for the whole section (all 5 tabs) — used by the admin.
export const getRaisePage = () => runRead("GET_RAISE_PAGE");
export const getCurriculum = () => runRead("GET_CURRICULUM");
export const listCalendar = () => runRead("LIST_CALENDAR");
export const listCertifications = () => runRead("LIST_CERTIFICATIONS");
export const listFaq = () => runRead("LIST_FAQ");
// Generic per-section page copy, returned as a nested map
// { section: { key: { value, type } } }. Any tab's editable text lives here;
// missing keys fall back to the component's built-in defaults. The Home tab's
// flywheel SVG is NOT stored.
export const getContent = () => runRead("GET_CONTENT");
// Contact Us list (alias + role, ordered). Name/photo resolved on the UI.
export const listContacts = () => runRead("LIST_CONTACTS");

/* ---------------- writes (admin) ----------------
 * Each returns the resolver result. Callers re-fetch after a write to pick up
 * the change (there is no cache to bust). */

export const upsertCourse = (data) => runWrite("UPSERT_COURSE", data);
export const deleteCourse = (courseId) => runWrite("DELETE_COURSE", { course_id: courseId });
// Set a matrix cell's tagline (by cell_id, or skill_set_id + tier_id which
// lazily creates the cell). Cells are never deleted from the UI.
export const upsertCell = (data) => runWrite("UPSERT_CELL", data);
export const upsertSession = (data) => runWrite("UPSERT_SESSION", data);
export const deleteSession = (sessionId) => runWrite("DELETE_SESSION", { session_id: sessionId });
export const upsertFaq = (data) => runWrite("UPSERT_FAQ", data);
export const deleteFaq = (faqId) => runWrite("DELETE_FAQ", { faq_id: faqId });
export const upsertFaqCategory = (data) => runWrite("UPSERT_FAQ_CATEGORY", data);
export const deleteFaqCategory = (categoryId) =>
  runWrite("DELETE_FAQ_CATEGORY", { category_id: categoryId });
// Save a batch of page-copy fields for one section (tab):
//   upsertContent("home", [{ key, value, type? }, ...])
// `type` is "text" (default) or "markdown". Any tab reuses this with its own
// section key.
export const upsertContent = (section, items) =>
  runWrite("UPSERT_CONTENT", { section, items });
// Contact Us CRUD (alias + role + sort order).
export const upsertContact = (data) => runWrite("UPSERT_CONTACT", data);
export const deleteContact = (contactId) =>
  runWrite("DELETE_CONTACT", { contact_id: contactId });

/* ---- Lookup lists that feed the editor dropdowns ----
 * A delete is refused by the backend when the value is still referenced (it
 * returns an in-use error the UI surfaces); otherwise it is removed. */
export const upsertProvider = (data) => runWrite("UPSERT_PROVIDER", data);
export const deleteProvider = (providerId) =>
  runWrite("DELETE_PROVIDER", { provider_id: providerId });
export const upsertPlatform = (data) => runWrite("UPSERT_PLATFORM", data);
export const deletePlatform = (platformId) =>
  runWrite("DELETE_PLATFORM", { platform_id: platformId });
export const upsertSkillSet = (data) => runWrite("UPSERT_SKILL_SET", data);
export const deleteSkillSet = (skillSetId) =>
  runWrite("DELETE_SKILL_SET", { skill_set_id: skillSetId });
